import { useContext } from 'react'
import type { AppDispatch } from '@redux/store'

import { launchModal } from '@domain-group/javascript-utils'
import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { baseURL } from '@functions/api'

import PageContext from '@contexts/pageContext'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as templatePageActions from '@redux/modules/templatePage'
import type { DeleteParams, EntityOptions, TemplatePageModuleState } from '@redux/modules/types'

import defaultFormState, { requiredFields } from '@models/templatePage'
import type {
  TaskListItemModel, TemplatePageModel, TemplatePageRequestOptions, TemplatePageNewPermissions,
} from '@models/types'

import mergeHashQueryParamsWithRedirect from '@functions/mergeHashQueryParamsWithRedirect'

export const generateUrls = (
  templatePage: Partial<TemplatePageModel> = {},
  options: { includeRedirect?: boolean } = {},
) => {
  const { includeRedirect } = options
  const { id } = templatePage

  return {
    archivedTemplatePagesIndexUrl: mergeHashQueryParamsWithRedirect({
      hash: '#/templatePages?hidden=true',
      includeRedirect,
    }),
    editTemplatePageUrl: mergeHashQueryParamsWithRedirect({
      hash: `#/templatePages/${id}/edit`,
      includeRedirect,
    }),
    templatePagesIndexUrl: mergeHashQueryParamsWithRedirect({
      hash: '#/templatePages?hidden=false',
      includeRedirect,
    }),
    templatePageDownloadPdfUrl: `${baseURL}/template_pages/${id}/pdf.json`,
  }
}

type GetPermissionCountParams = {
  key: 'agencies' | 'brand_themes' | 'products' | 'providers' | 'publications' | 'template_groups',
  permissionIds: {
    agencies: number[],
    brand_themes: number[],
    products: number[],
    providers: number[],
    publications: number[],
    template_groups: number[],
  },
}

const getPermissionCount = (params: GetPermissionCountParams) => {
  const { key, permissionIds } = params
  const idArray = permissionIds[key] ?? []
  return idArray.length
}

export const getPermissionCounts = (templatePage: Partial<TemplatePageModel> = {}) => {
  const permissionIds = templatePage?.permission_ids ?? {
    agencies: [],
    brand_themes: [],
    products: [],
    providers: [],
    publications: [],
    template_groups: [],
  }

  return {
    agencies: getPermissionCount({ key: 'agencies', permissionIds }),
    brand_themes: getPermissionCount({ key: 'brand_themes', permissionIds }),
    products: getPermissionCount({ key: 'products', permissionIds }),
    providers: getPermissionCount({ key: 'providers', permissionIds }),
    publications: getPermissionCount({ key: 'publications', permissionIds }),
    template_groups: getPermissionCount({ key: 'template_groups', permissionIds }),
  }
}

type CreateTemplatePageParams = {
  dispatch: AppDispatch,
  templatePageParams: Partial<TemplatePageModel>,
  requestOptions?: TemplatePageRequestOptions,
}

const createTemplatePage = (params: CreateTemplatePageParams) => {
  const { templatePageParams, dispatch, requestOptions } = params
  const { createTemplatePage: createFn } = templatePageActions

  return dispatch(createFn(templatePageParams, requestOptions))
}

type CreateTemplatePageFromTaskParams = {
  dispatch: AppDispatch,
  taskListItem: Partial<TaskListItemModel>,
  templatePageParams: Partial<TemplatePageModel>,
  requestOptions?: TemplatePageRequestOptions,
}

const createTemplatePageFromTask = (params: CreateTemplatePageFromTaskParams) => {
  const {
    dispatch, requestOptions, templatePageParams, taskListItem,
  } = params

  const { createTemplatePageFromTask: createFromTaskFn } = templatePageActions

  return dispatch(createFromTaskFn(templatePageParams, taskListItem, requestOptions))
}

type UpdateTemplatePageParams = {
  dispatch: AppDispatch,
  templatePage: TemplatePageModel,
  templatePageParams: Partial<TemplatePageModel>,
  requestOptions?: TemplatePageRequestOptions,
}

const updateTemplatePage = (params: UpdateTemplatePageParams) => {
  const {
    dispatch, templatePage, templatePageParams, requestOptions,
  } = params
  const { updateTemplatePage: updateFn } = templatePageActions

  const updatedParams = {
    id: templatePage.id,
    ...templatePageParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type BulkUpdateTemplatePagePermissionsParams = {
  dispatch: AppDispatch,
  templatePage: TemplatePageModel,
  permissions: TemplatePageNewPermissions,
  requestOptions?: TemplatePageRequestOptions,
}

const bulkUpdateTemplatePagePermissions = (params: BulkUpdateTemplatePagePermissionsParams) => {
  const {
    dispatch, permissions, requestOptions, templatePage,
  } = params
  const { bulkUpdateTemplatePagePermissions: updateFn } = templatePageActions

  return dispatch(updateFn(templatePage, permissions, requestOptions))
}

type DuplicateTemplatePageParams = {
  dispatch: AppDispatch,
  isNewVersion: boolean,
  requestOptions?: TemplatePageRequestOptions,
  templatePage: TemplatePageModel,
}

const duplicateTemplatePage = (params: DuplicateTemplatePageParams) => {
  const {
    dispatch, isNewVersion = false, requestOptions, templatePage,
  } = params
  const { duplicateTemplatePage: duplicateFn } = templatePageActions

  const mergedOptions = {
    ...requestOptions,
    new_version: isNewVersion,
  }

  return dispatch(duplicateFn(templatePage, mergedOptions))
}

type DeleteTemplatePageParams = {
  dispatch: AppDispatch,
  templatePage: DeleteParams<TemplatePageModel>,
}

const deleteTemplatePage = (params: DeleteTemplatePageParams) => {
  const { templatePage, dispatch } = params
  const { deleteTemplatePage: deleteFn } = templatePageActions

  return dispatch(deleteFn(templatePage))
}

type ToggleProductionReadyParams = {
  dispatch: AppDispatch,
  templatePage: TemplatePageModel,
}

const toggleProductionReady = (params: ToggleProductionReadyParams) => {
  const { templatePage, dispatch } = params
  const { toggleProductionReady: toggleProductionReadyFn } = templatePageActions

  return dispatch(toggleProductionReadyFn(templatePage))
}

type UploadTemplatePagePreviewParams = {
  dispatch: AppDispatch,
  templatePage: TemplatePageModel,
  attachment: File,
}

const uploadTemplatePagePreview = (params: UploadTemplatePagePreviewParams) => {
  const { dispatch, templatePage, attachment } = params
  const { uploadTemplatePagePreview: uploadFn } = templatePageActions

  return dispatch(uploadFn({ id: templatePage.id, attachment }))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useTemplatePageForm(
  templatePage: Partial<TemplatePageModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const templatePageForm = useForm(
    defaultFormState,
    { entity: templatePage, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [templatePage.id, templatePage.cache_key],
  )

  return {
    ...templatePageForm,
  }
}

// Return related entities
export const useRelations = (entity: Partial<TemplatePageModel> = {}) => {
  const { brand_id } = entity

  const { brands } = useSelector(reduxState => reduxState.entities)

  const brand = brand_id && brands[brand_id] ? brands[brand_id] : {}

  return {
    brand,
  }
}

type UseTemplatePageOptions = {
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useTemplatePage(
  initEntity: Pick<TemplatePageModel, 'id'> & Partial<TemplatePageModel>,
  options: UseTemplatePageOptions = {},
) {
  const { performHttpRequests, requestOptions } = options || {}

  const { entity: templatePage } = useLatestEntity(initEntity, 'templatePages')
  const { id, origin_template_id } = templatePage

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  useReduxAction(
    'templatePages',
    'loadTemplatePage',
    {
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, reqOptions: EntityOptions) => action(id, reqOptions),
      // @ts-expect-error
      shouldPerformFn: (entityReducer: TemplatePageModuleState) => {
        const { loadedIds } = entityReducer
        return performHttpRequests && !loadedIds?.includes(id)
      },
    },
  )

  const { creating, loading } = useSelector(reduxState => reduxState.templatePages)

  const createdByBrand = templatePage.designer_type === 'Brand'
  const hasOriginTemplatePage = !!origin_template_id

  return {
    callbacks: {
      assignTemplatePagePermissions: (stepKey: string) => launchModal({
        callbacks,
        modalKey: 'AssignTemplatePagePermissionsModal',
        payload: {
          stepKey,
          templatePage,
        },
      }),
      bulkUpdateTemplatePagePermissions: (
        permissions: TemplatePageNewPermissions,
        entityRequestOptions?: EntityOptions,
      ) => (
        bulkUpdateTemplatePagePermissions({
          dispatch,
          permissions,
          requestOptions: entityRequestOptions,
          // @ts-expect-error
          templatePage,
        })
      ),
      createTemplatePage: (
        templatePageParams: Partial<TemplatePageModel>,
        entityRequestOptions?: EntityOptions,
      ) => (
        createTemplatePage({ templatePageParams, dispatch, requestOptions: entityRequestOptions })
      ),
      createTemplatePageFromTask: (
        taskListItem: Partial<TaskListItemModel>,
        templatePageParams: Partial<TemplatePageModel>,
        entityRequestOptions?: EntityOptions,
      ) => (
        createTemplatePageFromTask({
          dispatch, taskListItem, templatePageParams, requestOptions: entityRequestOptions,
        })
      ),
      deleteTemplatePage: () => (
        deleteTemplatePage({ dispatch, templatePage })
      ),
      duplicateTemplatePage: (params: { entityRequestOptions?: EntityOptions, isNewVersion?: boolean }) => {
        const { entityRequestOptions, isNewVersion = false } = params

        return duplicateTemplatePage({
          // @ts-expect-error
          dispatch, isNewVersion, templatePage, requestOptions: entityRequestOptions,
        })
      },
      toggleProductionReady: () => (
        // @ts-expect-error
        toggleProductionReady({ dispatch, templatePage })
      ),
      manageTemplatePageActivity: () => launchModal({
        callbacks,
        modalKey: 'ManageEntityActivityModal',
        payload: {
          conversationTitle: `Template: #${templatePage.id}`,
          subjectId: templatePage.id,
          subjectType: 'TemplatePage',
        },
      }),
      viewArtworkInUse: () => {
        callbacks?.showArtworkInUseModal?.({ templatePage })
      },
      uploadTemplatePagePreview: (attachment: File) => (
        // @ts-expect-error
        uploadTemplatePagePreview({ dispatch, templatePage, attachment })
      ),
      updateTemplatePage: (
        templatePageParams: Partial<TemplatePageModel>,
        entityRequestOptions?: EntityOptions,
      ) => (
        updateTemplatePage({
          // @ts-expect-error
          templatePage, templatePageParams, dispatch, requestOptions: entityRequestOptions,
        })
      ),
      viewTemplatePageDetails: () => launchModal({
        callbacks,
        modalKey: 'ViewTemplatePageDetailsModal',
        payload: {
          templatePage,
        },
      }),
    },
    createdByBrand,
    creating,
    hasOriginTemplatePage,
    loading,
    permissionCounts: getPermissionCounts(templatePage),
    templatePage,
    urls: generateUrls(templatePage),
  }
}

export default useTemplatePage
